import { useMutation, useQueryClient } from 'react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '../../../../../designSystem/Button/Button';
import { ButtonColor, ButtonSize } from '../../../../../designSystem/Button/Button.types';
import { useCurrentUser } from '../../../../../../hooks/useCurrentUser/useCurrentUser';
import { UserENT } from '../../../../../../types/models/user/user.types';
import { useActiveNafList } from '../../../../../../hooks/fetchers/useActiveNafList/useActiveNafList';
import { usePlansENT } from '../../../../../../hooks/fetchers/usePlansENT/usePlansENT';
import { amountWithTVA } from '../../../../../../utils/helpers/price/amountWithTVA';
import {
  RegisterConfirmationStepFormMutation,
  RegisterConfirmationStepFormSchema,
} from './RegisterConfirmationBisStepForm.types';
import {
  REGISTER_NAF_QUERY_KEY,
  checkCodeQuery,
  registerNAFQuery,
  registerNAFandTransactionQuery,
  simulatePaymentQuery,
} from './RegisterConfirmationBisStepForm.query';
import RegisterPaymentModal from '../../RegisterConfirmationStep/RegisterPaymentModal/RegisterPaymentModal';
import RegisterStepButton from '../../../../../commons/RegisterStepButton/RegisterStepButton';
import ConfirmationBisRow from './ConfirmationBisRow/ConfirmationBisRow';
import { SelectOption } from '../../../../../commons/Select/Select.types';
import Select from '../../../../../commons/Select/Select';
import { Code } from '../../../../../../types/models/code/code.types';
import Input from '../../../../../commons/Input/Input';
import { useIsDev } from '../../../../../../hooks/useIsDev/useIsDev';
import { BaseResponse } from '../../../../../../types/api/api.types';
import { UseSignaturePaymentENTParams } from '../../../../../../hooks/fetchers/useSignaturePaymentENT/useSignaturePaymentENT.types';
import { signOut } from '../../../../../../store/authentication/authenticationActions/authenticationActions';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import PhonePaymentModal from '../PhonePaymentModal/PhonePaymentModal';

export default () => {

  const navigate = useNavigate();

  const dispatch = useDispatch();

  const queryClient = useQueryClient();

  const [isPaymentModalOpen, setPaymentModalOpen] = useState(false);
  const [isPhonePaymentModalOpen, setPhonePaymentModalOpen] = useState(false);

  const currentUser = useCurrentUser() as UserENT;

  const IS_DEV = useIsDev();

  const { data: NAFList } = useActiveNafList();

  const { data: plansENT } = usePlansENT();

  const [choosedNAFCodes, setChoosedNAFCodes] = useState<(SelectOption | null)[]>([null]);
  const [duration, setDuration] = useState<1 | 2 | 3>();
  const [codeInputValue, setCodeInputValue] = useState<string>('');
  const [code, setCode] = useState<Code>();

  const onChange = useCallback(
    (indexToUpdate: number, data: SelectOption) => {
      const updatedItems = choosedNAFCodes.reduce(
        (acc, item, index) => {
          if (index === indexToUpdate) {
            return [...acc, data];
          } else {
            return [...acc, item];
          }
        },
        [] as (SelectOption | null)[]
      );
      setChoosedNAFCodes(updatedItems);
    },
    [choosedNAFCodes]
  );

  const onAdd = useCallback(() => {
    setChoosedNAFCodes([...choosedNAFCodes, null]);
  }, [choosedNAFCodes]);

  const onRemove = useCallback(
    (indexToRemove: number) => {
      const newItems = choosedNAFCodes.reduce(
        (acc, item, index) => {
          if (index !== indexToRemove) {
            return [...acc, item];
          } else {
            return acc;
          }
        },
        [] as (SelectOption | null)[]
      );
      setChoosedNAFCodes(newItems);
    },
    [choosedNAFCodes]
  );

  const currentPlan = useMemo(
    () => plansENT?.find((plan) => plan.id === currentUser.id_plan),
    [plansENT]
  );

  const NAFCodes = useMemo(() => choosedNAFCodes.map((item) => item?.value), [choosedNAFCodes]);

  const isValid = useMemo(() => NAFCodes.filter((item) => !item)?.length === 0, [NAFCodes]);

  const amount = useMemo(() => {
    if (NAFCodes && NAFCodes.length > 3 && currentPlan && duration) {
      return 30 * (NAFCodes.length - 3) + currentPlan[`price_${duration}y`] / 100;
    } else if (currentPlan && duration) {
      return currentPlan[`price_${duration}y`] / 100;
    } else {
      return null;
    }
  }, [NAFCodes, currentPlan, duration]);

  const amountWithDiscount = useMemo(() => {
    if (amount && code) {
      return amount - amount * (code.percentage / 100);
    }
  }, [amount, code, duration]);

  const updatedAmountTTC = useMemo(
    () => (amountWithDiscount ? amountWithTVA(amountWithDiscount) : amountWithTVA(amount || 0)),
    [amount, amountWithDiscount]
  );

  useEffect(() => {
    console.log({amount, duration})
  }, [amount, duration])

  const { mutate } = useMutation<
    Awaited<ReturnType<RegisterConfirmationStepFormMutation>>,
    Error,
    RegisterConfirmationStepFormSchema['fieldsOfActivity']
  >((data) => registerNAFQuery(data), {
    mutationKey: REGISTER_NAF_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: async () => {
      if (!IS_DEV) {
        setPaymentModalOpen(true);
      }
      // to integrate on RegisterPaymentModal
      // api.setHeader('authorization', `Bearer ${accessToken}`);
      // removeCookie('token', { path: '/' });
      // setCookie('token', accessToken, { expires: moment().add(60, 'minutes').toDate(), path: '/' });
      // setProfile({ ...currentUser, id_step: getStepIdFromENTStep(RegisterStepsList.Confirmation) })(
      //   dispatch
      // );

      // navigate('/register/PUB/step/user')
    },
    onError: async (error) => {
      alert(error.message);
    },
  });

  const { mutate: registerNAF } = useMutation<
    Awaited<ReturnType<RegisterConfirmationStepFormMutation>>,
    Error,
    any
  >((data) => registerNAFandTransactionQuery(data), {
    mutationKey: REGISTER_NAF_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: async () => {
      setPhonePaymentModalOpen(true)
    },
    onError: async (error) => {
      alert(error.message);
    },
  });

  const { mutate: simulatePayment } = useMutation<
    Awaited<BaseResponse>,
    Error,
    UseSignaturePaymentENTParams
  >((data) => simulatePaymentQuery(data), {
    mutationKey: REGISTER_NAF_QUERY_KEY,
    onMutate: () => queryClient.cancelMutations(),
    onSuccess: () => {
      signOut()(dispatch)
      navigate('/login/entreprise')
    },
    onError: async (error) => {
      alert(error.message);
    },
  });

  const { mutate: checkCode } = useMutation<Awaited<Code>, Error, string>(
    (data) => checkCodeQuery(data),
    {
      mutationKey: REGISTER_NAF_QUERY_KEY,
      onMutate: () => queryClient.cancelMutations(),
      onSuccess: async (code) => {
        setCode(code);
      },
      onError: async (error) => {
        alert(error.message);
      },
    }
  );

  return (
    <>
      <form className="form" style={{ width: '75%' }}>
        <div style={{ marginBottom: 'var(--spacing-sToM)' }}>
          <Select
            placeholder={'Sélectionnez la durée de votre abonnement'}
            label="Durée de l'abonnement"
            id="subscription"
            required
            options={[
              {
                value: '1',
                label: "1 an d'abonnement",
              },
              {
                value: '2',
                label: "2 ans d'abonnement (le plus utilisé)",
              },
              {
                value: '3',
                label: "3 ans d'abonnement (le plus économique)",
              },
            ]}
            // @ts-ignore
            onChange={(selectedOption: SelectOption) => {
              const value = parseInt(selectedOption.value);
              if (value === 1 || value === 2 || value === 3) {
                setDuration(value);
              }
            }}
          />
          {duration && duration > 1 && currentPlan && (
            <span className="register__payment-save">
              Vous économisez{' '}
              {(currentPlan.price_1y * duration - currentPlan[`price_${duration}y`]) / 100} €
            </span>
          )}
        </div>
        {choosedNAFCodes.map((_, index) => (
          <ConfirmationBisRow
            key={index}
            isLast={index === choosedNAFCodes.length - 1}
            {...{ index, onChange, onAdd, onRemove, NAFList, choosedNAFCodes }}
          />
        ))}
        <div className="form__row register__confirmation-bis-row register__payment-code">
          <div style={{ flex: 1 }} className="flex-column">
            <Input
              name="code"
              label="Code promo"
              onChange={(e) => setCodeInputValue(e.target.value)}
              {...(code && {
                divProps: {
                  style: {
                    marginBottom: 0,
                  },
                },
              })}
            />
            {code && <span className="register__payment-save">{code.description}</span>}
          </div>
          <button
            onClick={(e) => {
              e.preventDefault();
              checkCode(codeInputValue);
            }}>
            Ajouter
          </button>
        </div>
        {amount && (
          <div className="register__payment-price">
            <div className="register__payment-price-row">
              <span>Abonnement annuel</span>
              <span>{amount?.toFixed(2)}€</span>
            </div>
            {NAFCodes.length > 3 && (
              <div className="register__payment-price-row">
                <span>Domaines supplémentaires</span>
                <span>{NAFCodes.length - 3}</span>
              </div>
            )}
            <div className="register__payment-price-row">
              <span>TVA 20%</span>
              <span>{(amountWithTVA(amount) - amount).toFixed(2)}€</span>
            </div>
            {code && (
              <div className="register__payment-price-row">
                <span style={{ color: 'var(--success)' }}>
                  Code promo: <strong>{code.code}</strong>
                </span>
                <span style={{ color: 'var(--success)' }}>-{code.percentage}%</span>
              </div>
            )}
            <div className="register__payment-price-row register__payment-price-row--total">
              <span>Total TTC</span>
              {amountWithDiscount ? (
                <>
                  <span style={{ color: 'var(--success)' }}>
                    {amountWithTVA(amountWithDiscount).toFixed(2)}€{' '}
                    <span style={{ color: 'red', textDecoration: 'line-through' }}>
                      {amountWithTVA(amount).toFixed(2)}€
                    </span>
                  </span>
                </>
              ) : (
                <span>{amountWithTVA(amount).toFixed(2)}€</span>
              )}
            </div>
          </div>
        )}
        <RegisterStepButton isCompany />
        <Button
          style={{ marginBottom: 'var(--spacing-s)' }}
          color={ButtonColor.Secondary}
          disabled={!isValid || !amount || !duration}
          onClick={(e) => {
            e.preventDefault();
            registerNAF({
              fieldsOfActivity: NAFCodes as string[],
              duration: duration || 1,
              code,
              currentTime: moment().utc().format('YYYYMMDDHHmmss')
            });
          }}
        >
          Payer par téléphone
          {/* {`Payer par téléphone · ${
            amountWithDiscount
              ? amountWithTVA(amountWithDiscount).toFixed(2)
              : amountWithTVA(amount || 0).toFixed(2)
          }€`} */}
        </Button>
        <Button
          style={{ alignSelf: 'unset' }}
          onClick={(e) => {
            e.preventDefault();
            mutate(NAFCodes);
            if (IS_DEV) {
              simulatePayment({
                fieldsOfActivity: NAFCodes as string[],
                duration: duration || 1,
                code,
                currentTime: moment().utc().format('YYYYMMDDHHmmss')
              })
            }
          }}
          size={ButtonSize.Large}
          disabled={!isValid || !amount || !duration}
          color={ButtonColor.Secondary}>
            Payer par CB
          {/* {`Payer par CB · ${
            amountWithDiscount
              ? amountWithTVA(amountWithDiscount).toFixed(2)
              : amountWithTVA(amount || 0).toFixed(2)
          }€`} */}
        </Button>
      </form>
      <PhonePaymentModal 
        isOpen={isPhonePaymentModalOpen}
        setOpen={setPhonePaymentModalOpen}
      />
      {isValid && isPaymentModalOpen && duration && (amount || amountWithDiscount) && (
        <RegisterPaymentModal
          isOpen={isPaymentModalOpen}
          setOpen={setPaymentModalOpen}
          closeOnClickOutside
          paymentData={{
            amount: parseFloat(updatedAmountTTC.toFixed(2)),
            fieldsOfActivity: NAFCodes as string[],
            duration,
            code,
          }}
        />
      )}
    </>
  );
};
